/**
 * Chapter Database - Maps all available ZSV chapters with metadata
 * Used for intelligent mission generation and rotation
 */

export interface ChapterInfo {
    id: string;
    title: string;
    topic: 'filosofie' | 'sociologie' | 'politologie' | 'ekonomie' | 'psychologie';
    isFree: boolean;
}

export const CHAPTER_DATABASE: ChapterInfo[] = [
    // ===== FILOSOFIE (3 chapters) =====
    {
        id: 'filosoficke-smery',
        title: 'Základní filozofické směry a proudy',
        topic: 'filosofie',
        isFree: true
    },
    {
        id: 'filosofie-vedy',
        title: 'Filosofie vědy',
        topic: 'filosofie',
        isFree: false
    },
    {
        id: 'ceska-filozofie-kapitola',
        title: 'Česká filozofie',
        topic: 'filosofie',
        isFree: false
    },

    // ===== SOCIOLOGIE (4 chapters) =====
    {
        id: 'vznik-a-vyvoj-sociologie',
        title: 'Vznik a vývoj sociologie',
        topic: 'sociologie',
        isFree: true
    },
    {
        id: 'kultura-a-socializace',
        title: 'Kultura a socializace',
        topic: 'sociologie',
        isFree: false
    },
    {
        id: 'soc-vyzkum-metody-pojmy',
        title: 'Metody a pojmy v sociologickém výzkumu',
        topic: 'sociologie',
        isFree: false
    },
    {
        id: 'socialni-struktura-role-deviace',
        title: 'Sociální struktura, role a deviace',
        topic: 'sociologie',
        isFree: false
    },

    // ===== POLITOLOGIE (10 most relevant chapters) =====
    {
        id: 'f4d0e5f6-b7c8-4901-d2e3-f4a5b6c7d8e9',
        title: 'Politické strany a stranické systémy',
        topic: 'politologie',
        isFree: true
    },
    {
        id: 'd2b8c3e4-f5a6-4789-b0c1-d2e3f4a5b6c7',
        title: 'Politický systém České republiky',
        topic: 'politologie',
        isFree: false
    },
    {
        id: 'k9i5j0e1-g2h3-9456-i7j8-k9e0f1g2h3i4',
        title: 'Instituce a volební systémy',
        topic: 'politologie',
        isFree: false
    },
    {
        id: 'h6f2g7b8-d9e0-6123-f4g5-h6b7c8d9f0g1',
        title: 'Politické systémy a režimy',
        topic: 'politologie',
        isFree: false
    },
    {
        id: 'j8h4i9d0-f1g2-8345-h6i7-j8d9f0g1h2i3',
        title: 'Teorie státu a politické ideologie',
        topic: 'politologie',
        isFree: false
    },
    {
        id: 'g5e1f6a7-c8d9-5012-e3f4-g5a6b7c8d9f0',
        title: 'Mezinárodní vztahy a geopolitika',
        topic: 'politologie',
        isFree: false
    },
    {
        id: 'evropska-integrace-kapitola',
        title: 'Historie a vývoj evropské integrace',
        topic: 'politologie',
        isFree: false
    },
    {
        id: 'c1a9b2d3-e4f5-4678-9a0b-1c2d3e4f5a6b',
        title: 'Instituce a rozhodovací procesy EU',
        topic: 'politologie',
        isFree: false
    },
    {
        id: 'pravo-eu-kapitola',
        title: 'Právo EU a současné výzvy',
        topic: 'politologie',
        isFree: false
    },
    {
        id: 'schengen-politiky-eu-kapitola',
        title: 'Schengen a hlavní politiky EU',
        topic: 'politologie',
        isFree: false
    },

    // ===== EKONOMIE (2 chapters) =====
    {
        id: 'penize-a-bankovnictvi',
        title: 'Peníze a bankovnictví',
        topic: 'ekonomie',
        isFree: true
    },
    {
        id: 'ekonomicke-proudy-historie',
        title: 'Základní myšlenkové proudy ekonomie',
        topic: 'ekonomie',
        isFree: false
    },

    // ===== PSYCHOLOGIE (8 most relevant chapters) =====
    {
        id: 'uvod-do-psychologie',
        title: 'Úvod do psychologie',
        topic: 'psychologie',
        isFree: true
    },
    {
        id: 'chapter-psych-schools-01',
        title: 'Psychologické směry a přístupy',
        topic: 'psychologie',
        isFree: false
    },
    {
        id: 'kognitivni-procesy',
        title: 'Kognitivní procesy',
        topic: 'psychologie',
        isFree: false
    },
    {
        id: 'inteligence-a-typologie-osobnosti',
        title: 'Inteligence a typologie osobnosti',
        topic: 'psychologie',
        isFree: false
    },
    {
        id: 'podminovani-a-uceni',
        title: 'Podmiňování a učení',
        topic: 'psychologie',
        isFree: false
    },
    {
        id: 'struktury-vedomi-a-nevedomi',
        title: 'Struktury vědomí a nevědomí',
        topic: 'psychologie',
        isFree: false
    },
    {
        id: 'psychopatologie-a-poruchy-v2-full',
        title: 'Psychopatologie a poruchy',
        topic: 'psychologie',
        isFree: false
    },
    {
        id: 'chapter-social-psych-01-revised',
        title: 'Sociální psychologie',
        topic: 'psychologie',
        isFree: false
    },
];

/**
 * Get all chapters for a specific topic, filtered by user tier
 */
export function getChaptersByTopic(
    topic: string,
    isPro: boolean
): ChapterInfo[] {
    return CHAPTER_DATABASE
        .filter(ch => ch.topic === topic && (isPro || ch.isFree));
}

/**
 * Get next unseen chapter for a topic, with rotation
 * Returns first available chapter if all have been seen
 */
export function getNextChapter(
    topic: string,
    completedChapters: string[] = [],
    isPro: boolean = false
): ChapterInfo | null {
    const availableChapters = getChaptersByTopic(topic, isPro);

    if (availableChapters.length === 0) {
        return null;
    }

    // Find first unseen chapter
    const unseenChapter = availableChapters.find(
        ch => !completedChapters.includes(ch.id)
    );

    // If all chapters seen, restart from first
    return unseenChapter || availableChapters[0];
}

/**
 * Get chapter info by ID
 */
export function getChapterById(chapterId: string): ChapterInfo | undefined {
    return CHAPTER_DATABASE.find(ch => ch.id === chapterId);
}
